import type { Dispatch, SetStateAction, VoidFunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as _ from 'lodash';
import ContentLoader from 'react-content-loader';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import { Section } from '../../../shared/vulns/Drawer';
import DetailsPanel, { DetailsPanelList } from '../../../generic/DetailsPanel';
import styles from './Licensing.module.less';
import type { SingleRevisionDisplayMode } from '../../../modals/ModalDisplayLicense';
import ModalDisplayLicense from '../../../modals/ModalDisplayLicense';
import { useIssueDetailsModals, type UseDrawerModalsReturn } from '../IssueDetailsModalsContext';
import { plural } from '../../../../../shared/helpers';
import type { RevisionLicense } from '../../../../types';

type LicenseProps = {
  shouldDisplaySeparator: boolean;
  packageRevisionLocator?: string;
  viewLicenseModal: UseDrawerModalsReturn['viewLicenseModal'];
  revisionLicenses: RevisionLicense[];
  licenseId: string;
  setDisplayMode: Dispatch<SetStateAction<SingleRevisionDisplayMode | null>>;
};

/**
 * Display the license as a link. When user clicks on it, it displays the view license modal.
 */
const LicenseLink = ({
  shouldDisplaySeparator,
  packageRevisionLocator,
  viewLicenseModal,
  revisionLicenses,
  setDisplayMode,
  licenseId,
}: LicenseProps) => {
  return (
    <button
      type="button"
      className={styles.license}
      onClick={() => {
        if (packageRevisionLocator) {
          // Need to track this state in the drawerModals to prevent
          // the drawer closing unexpectedly.
          viewLicenseModal.onOpen();
          setDisplayMode({
            mode: 'single',
            revisionId: packageRevisionLocator,
            revisionLicenses,
          });
        }
      }}
    >
      {licenseId}
      {shouldDisplaySeparator && ','}
    </button>
  );
};

/**
 * LicensingIssueDetailsLicenses displays the list of licenses groups
 * for the package where the licensing issue was found.
 *
 * Note that these may include may other licenses than the one
 * which created this issue, as this is all the license groups
 * for the package revision.
 */
const LicensingIssueDetailsLicenses: VoidFunctionComponent = () => {
  const dispatch = useDispatch();
  const [displayMode, setDisplayMode] = useState<SingleRevisionDisplayMode | null>(null);
  const declaredLicenses = useSelector(IssuesV2Store.selectors.getDeclaredIssueLicenses);
  const discoveredLicenses = useSelector(IssuesV2Store.selectors.getDiscoveredIssueLicenses);
  const issueLicensesAsyncStatus = useSelector(IssuesV2Store.selectors.getIssueLicensesAsyncStatus);
  const packageRevisionLocator = useSelector(IssuesV2Store.selectors.getIssuePackageRevisionLocator);
  const { viewLicenseModal } = useIssueDetailsModals();

  useEffect(() => {
    if (packageRevisionLocator) {
      dispatch(IssuesV2Store.actions.fetchIssueLicensesAction(packageRevisionLocator));
    }
  }, [dispatch, packageRevisionLocator]);

  if (issueLicensesAsyncStatus.isLoading) {
    return (
      <ContentLoader width="100%" height="140">
        <rect x="0" y="0" rx="3" ry="3" width="100%" height="140" />
      </ContentLoader>
    );
  }

  //  Do not render any UI if there are no licenses to display.
  if (!declaredLicenses.length && !discoveredLicenses.length) {
    return null;
  }

  return (
    <Section>
      <DetailsPanel id="issue-details-licenses-section" target="All licenses">
        <DetailsPanelList
          rows={_.compact([
            declaredLicenses.length > 0 && {
              label: plural(declaredLicenses.length, 'Declared License'),
              content: (
                <div className={styles.licenseList}>
                  {declaredLicenses.map((license, index) => (
                    <LicenseLink
                      licenseId={license.signature}
                      revisionLicenses={license.licenses}
                      setDisplayMode={setDisplayMode}
                      packageRevisionLocator={packageRevisionLocator}
                      shouldDisplaySeparator={index !== declaredLicenses.length - 1}
                      viewLicenseModal={viewLicenseModal}
                    />
                  ))}
                </div>
              ),
            },
            discoveredLicenses.length > 0 && {
              label: plural(declaredLicenses.length, 'Discovered License'),
              content: (
                <div className={styles.licenseList}>
                  {discoveredLicenses.map((license, index) => (
                    <LicenseLink
                      licenseId={license.signature}
                      revisionLicenses={license.licenses}
                      setDisplayMode={setDisplayMode}
                      packageRevisionLocator={packageRevisionLocator}
                      shouldDisplaySeparator={index !== discoveredLicenses.length - 1}
                      viewLicenseModal={viewLicenseModal}
                    />
                  ))}
                </div>
              ),
            },
          ])}
        />
      </DetailsPanel>
      <ModalDisplayLicense
        displayMode={displayMode}
        onHide={() => {
          setDisplayMode(null);
          viewLicenseModal.onClose();
        }}
      />
    </Section>
  );
};

/**
 *  Simple container which will not render the LicensingIssueDetailsLicenses
 *  if this is an unlicensed dependency. Done as a container so that we can
 *  avoid needlessly fetching the /license-groups.
 */
const LicensingIssueDetailsLicensesContainer: VoidFunctionComponent = () => {
  const isUnlicensed = useSelector(IssuesV2Store.selectors.getIsIssueDetailsUnlicensed);

  /**
   *  Do not show the licenses if the license issue is "unlicensed".
   *  Technically there should not be any licenses anyway, but we do
   *  not want to render anything, even if this is the case.
   *
   *  One case where there would be licenses would be if the package
   *  was manually corrected and a license was added. The team decided
   *  that it would be confusing to the user if we displayed a list
   *  of licenses in the
   */
  if (isUnlicensed) {
    return null;
  }

  return (
    <LicensingIssueDetailsLicenses />
  );
};

export default LicensingIssueDetailsLicensesContainer;
